<script setup>
import { ref, computed, defineProps, defineExpose } from 'vue';
import { useStorage } from '@/composables/use-storage';
import { useLayoutComponents } from '@/composables/layout';
import ProfileAvatar from './ProfileAvatar.vue';

const { StorageCounter } = useLayoutComponents();

const props = defineProps({
  creator: {
    type: Object,
    required: true
  },
  showStorage: {
    type: Boolean,
    default: true,
  },
  showAvatar: {
    type: Boolean,
    default: true,
  },
  showName: {
    type: Boolean,
    default: true,
  },
});

// Composables
const { userInfo, hasPermission } = useStorage();


// State
const showCopied = ref(false);
const profilePicture = localStorage.getItem('profilePicture');

// Computed
const formattedWalletAddress = computed(() => {
  const walletAddress = props.creator.wallet;
  if (!walletAddress) return '';
  return `${walletAddress.slice(0, 7)}...${walletAddress.slice(-5)}`;
});

// Methods
const copyWallet = () => {
  navigator.clipboard.writeText(props.creator.wallet);
  showCopied.value = true;
  setTimeout(() => {
    showCopied.value = false;
  }, 2000);
};

const storageCounterRef = ref(null);

const refreshStorage = async () => {
  if (storageCounterRef.value && userInfo.value.seller) {
    await storageCounterRef.value.refreshStorageData();
  }
};

defineExpose({ refreshStorage });
</script>

<template>
  <div class="creator-profile__header">
    
    <ProfileAvatar
      v-if="props.showAvatar"
      :profilePic="userInfo?.profile_pic"
      :profilePicture="profilePicture"
      size="md"
    />

    <div class="creator-profile__info">
      
      <div class="creator-profile__name-row" v-if="props.showName">
        <h1 class="creator-profile__name">{{ creator.name }}</h1>
      </div>
      
      <div v-if="creator.wallet" class="creator-profile__wallet">
        <div class="creator-profile__wallet-address-container">
          <span class="creator-profile__wallet-label">Wallet Address:</span>
          
          <span class="creator-profile__wallet-address">{{ formattedWalletAddress }}</span>

          <button class="creator-profile__copy-btn" @click="copyWallet">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linejoin="round" stroke-width="2"
              d="M9 8v3a1 1 0 0 1-1 1H5m11 4h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-7a1 1 0 0 0-1 1v1m4 3v10a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7.13a1 1 0 0 1 .24-.65L7.7 8.35A1 1 0 0 1 8.46 8H13a1 1 0 0 1 1 1Z" />
            </svg>
            <span v-if="showCopied" class="creator-profile__copied-text">
              Copied!
            </span>
            <span v-else class="creator-profile__hover-text">
              Click to copy
            </span>
          </button>
          
        </div>
        
      </div>
      
    </div>
    
    <StorageCounter
      v-if="hasPermission('datasets') && props.showStorage"
      ref="storageCounterRef"
      :creator="creator"
    />

  </div>
</template>

<style scoped lang="postcss">
.creator-profile__header {
  @apply flex flex-col sm:flex-row items-center gap-4 sm:gap-6;
}

.creator-profile__info {
  @apply flex-1;
}

.creator-profile__name-row {
  @apply w-full flex flex-col sm:flex-row items-center justify-between gap-4;
}

.creator-profile__name {
  @apply text-xl sm:text-2xl font-bold text-gray-800 dark:text-white tracking-tight;
}

.creator-profile__edit-btn {
  @apply px-6 py-2 rounded-lg text-sm font-medium transition-colors dark:hover:border-purple-500/50 dark:hover:text-purple-400 dark:bg-white/10 dark:text-white dark:border-white/20 hover:border-purple-300 hover:text-purple-600 bg-white text-gray-700 border border-gray-200;
}

.creator-profile__wallet {
  @apply flex flex-col sm:flex-row items-center gap-2 text-sm text-gray-500 dark:text-gray-400 my-1 justify-center sm:justify-between;
}

.creator-profile__wallet-address-container {
  @apply flex flex-row items-center gap-2 text-sm text-gray-500 dark:text-gray-400 my-1 justify-center sm:justify-start whitespace-nowrap;
}

.creator-profile__wallet-label {
  @apply font-medium text-gray-700 dark:text-white whitespace-nowrap;
}

.creator-profile__wallet-address {
  @apply font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap;
}

.creator-profile__copy-btn {
  @apply p-1 relative flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-purple-500 transition-colors;
}

.creator-profile__copied-text {
  @apply text-sm font-medium pl-2 absolute whitespace-nowrap hidden xs:block left-full text-emerald-600 dark:text-emerald-500;
  animation: fadeInOut 2s ease-in-out;
}

.creator-profile__hover-text {
  @apply text-sm font-medium pl-2 absolute whitespace-nowrap hidden xs:block left-full text-gray-500 dark:text-gray-400 opacity-0 transition-opacity duration-200;
}

.creator-profile__copy-btn:hover .creator-profile__hover-text {
  @apply opacity-100;
}
</style>